import React from "react";

// Custom Tailwind button — replaces CoreUI's CButton. Glass-token aware (accent
// flips light/dark). Variants: solid accent ("primary") and bordered ("outline").
type ButtonVariant = "primary" | "outline" | "ghost" | "danger";

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
}

// NOTE: Tailwind preflight is OFF (coexistence), so a raw <button> keeps the
// browser's default grey background + border. The base resets that
// (appearance-none + transparent bg/border); variants then opt back in.
const BASE =
  "tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:rounded-xl! tw:px-4 tw:py-2 tw:text-sm tw:font-semibold tw:transition-colors tw:cursor-pointer tw:appearance-none tw:border tw:border-transparent tw:bg-transparent tw:disabled:opacity-50 tw:disabled:cursor-not-allowed";

const VARIANTS: Record<ButtonVariant, string> = {
  // GLASSY primary (per design): a frosted accent-tinted pill, not a solid blue
  // fill — translucent accent bg + accent border + accent text + soft blur. `!`
  // beats the BASE reset's transparent bg/border (same-layer cascade).
  primary:
    "tw:bg-accent/15! tw:text-accent! tw:border-accent/40! tw:backdrop-blur-sm tw:hover:bg-accent/25",
  // border-accent needs `!` for the same reason as primary's bg: the BASE reset's
  // tw:border-transparent otherwise wins, leaving the outline button borderless.
  outline: "tw:border-accent! tw:text-accent! tw:hover:bg-accent/10",
  ghost: "tw:text-text-muted! tw:hover:text-text! tw:hover:glass-hover",
  danger:
    "tw:bg-rose-500/15! tw:text-rose-500! tw:border-rose-500/40! tw:backdrop-blur-sm tw:hover:bg-rose-500/25",
};

const Button = ({
  variant = "primary",
  block = false,
  className = "",
  children,
  ...rest
}: ButtonProps) => (
  <button
    className={[BASE, VARIANTS[variant], block ? "tw:w-full" : "", className].join(" ")}
    {...rest}
  >
    {children}
  </button>
);

export default Button;
